/**
 * ShippingAddressDialog
 * Reusable structured-address collector for the bulk-creator pipeline.
 *
 * - `row`     : a bulk_creators row object (id, creator_name, shipping_address)
 * - `onClose` : called on cancel / X
 * - `onSaved` : called after a successful PATCH
 *
 * Saves to PATCH /api/campaigns/{row_id}/shipping-address (CM/admin only).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { MapPin, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  full_name: '',
  phone: '',
  address_line_1: '',
  address_line_2: '',
  city: '',
  pincode: '',
  state: '',
};

const LABEL_CLS = 'text-[10px] font-semibold uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 block mb-1.5 font-mono';
const INPUT_CLS = 'bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500';

export const ShippingAddressDialog = ({ row, onClose, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const open = Boolean(row);

  useEffect(() => {
    if (!row) {
      setForm(EMPTY);
      return;
    }
    const sa = row.shipping_address;
    if (sa && typeof sa === 'object') {
      setForm({ ...EMPTY, ...sa });
    } else {
      setForm({ ...EMPTY, full_name: row.creator_name || '' });
    }
  }, [row]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    // Light validation. Backend re-validates via Pydantic.
    const required = ['full_name', 'phone', 'address_line_1', 'city', 'pincode', 'state'];
    for (const k of required) {
      if (!form[k] || !String(form[k]).trim()) {
        toast.error(`${k.replace(/_/g, ' ')} is required`);
        return;
      }
    }
    setSaving(true);
    try {
      await axios.patch(
        `${API_URL}/api/campaigns/${row.id}/shipping-address`,
        form,
        { withCredentials: true },
      );
      toast.success('Address saved');
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save address');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving && !o) onClose?.(); }}>
      <DialogContent className="bg-white dark:bg-[#0A0B0D] border border-black/[0.10] dark:border-white/10 text-[#0F0F0F] dark:text-white max-w-lg" data-testid="shipping-address-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <MapPin size={18} className="text-[#FF451A]" /> Shipping Address
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6B6B6B] dark:text-zinc-500">
            {row?.creator_name ? `Collected for ${row.creator_name}` : ''}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <label className={LABEL_CLS}>Full Name</label>
            <Input value={form.full_name} onChange={(e) => update('full_name', e.target.value)} className={INPUT_CLS} data-testid="address-full-name" required />
          </div>
          <div>
            <label className={LABEL_CLS}>Phone</label>
            <Input value={form.phone} onChange={(e) => update('phone', e.target.value)} className={INPUT_CLS} data-testid="address-phone" required />
          </div>
          <div>
            <label className={LABEL_CLS}>Address Line 1</label>
            <Input value={form.address_line_1} onChange={(e) => update('address_line_1', e.target.value)} className={INPUT_CLS} data-testid="address-line-1" required />
          </div>
          <div>
            <label className={LABEL_CLS}>Address Line 2</label>
            <Input value={form.address_line_2} onChange={(e) => update('address_line_2', e.target.value)} className={INPUT_CLS} data-testid="address-line-2" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={LABEL_CLS}>City</label>
              <Input value={form.city} onChange={(e) => update('city', e.target.value)} className={INPUT_CLS} data-testid="address-city" required />
            </div>
            <div>
              <label className={LABEL_CLS}>State</label>
              <Input value={form.state} onChange={(e) => update('state', e.target.value)} className={INPUT_CLS} data-testid="address-state" required />
            </div>
            <div>
              <label className={LABEL_CLS}>Pincode</label>
              <Input value={form.pincode} onChange={(e) => update('pincode', e.target.value)} className={INPUT_CLS} data-testid="address-pincode" required />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={saving} className="bg-[#FF451A] hover:bg-[#FF451A]/90 text-[#0F0F0F] dark:text-white rounded-full px-5" data-testid="address-save-btn">
              <Save size={14} className="mr-1.5" /> {saving ? 'Saving…' : 'Save Address'}
            </Button>
            <Button type="button" onClick={() => onClose?.()} disabled={saving} className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#0F0F0F] dark:text-white hover:bg-white/5 rounded-full px-5">
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
